import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { differenceInHours, formatDate, isValid, parseISO } from 'lib/helpers/dateUtils';
import useTimezone from 'lib/hooks/useTimezone';

import {
  DEFAULT_BASE_URL_SEGMENTS,
  INTRO_STEP_DEFAULT_DEFINITION,
  REVIEW_STEP_DEFAULT_DEFINITION
} from '../lib/constants';

import useStepper from './useStepper';

const buildDueDateDescription = (dueDate, timezone) => {
  if (!dueDate) return null;
  const parsed = parseISO(dueDate);
  if (!isValid(parsed)) return null;
  const hours = differenceInHours(parsed, new Date());
  const variant = hours <= 24 ? 'danger' : hours <= 48 ? 'warning' : 'default';
  return { text: `Due Date: ${formatDate(dueDate, timezone, 'MMM dd')}`, variant };
};

export const useStepperOrchestration = ({
  wizardState,
  wizardKind,
  hiddenSubstepCodes,
  navigateToCode,
  componentStepsConfig,
  onNavigateToCodeHandled,
  baseUrlSegments = DEFAULT_BASE_URL_SEGMENTS,
  wrapWithIntroAndReview = true,
  skipInitialUrlSync = false,
  syncWithUrl = true,
  includeIntro = true,
  includeReview = true
}) => {
  const { instance = {}, steps: initialSteps = [], phases = null } = wizardState || {};
  const location = useLocation();
  const timezone = useTimezone();

  const baseUrl = useMemo(() => {
    const pathParts = location.pathname.split('/');
    for (const segment of baseUrlSegments) {
      const segmentIndex = pathParts.indexOf(segment);
      if (segmentIndex > -1) {
        return pathParts.slice(0, segmentIndex + 1).join('/');
      }
    }
    return location.pathname;
  }, [location.pathname, baseUrlSegments]);

  const shouldIncludeIntro = wrapWithIntroAndReview && includeIntro;
  const shouldIncludeReview = wrapWithIntroAndReview && includeReview;
  const orchestratedSteps = useMemo(() => {
    const instanceDueDate = instance?.hard_go_live_date;
    const resolveDescription = (code, fallback, dueDate) =>
      componentStepsConfig?.[code]?.sidebarDescription ??
      fallback ??
      buildDueDateDescription(dueDate ?? instanceDueDate, timezone);

    const enrichedSteps = initialSteps.map((step) => ({
      ...step,
      sidebar_description: resolveDescription(step.code, step.sidebar_description, step.due_date),
      substeps: (step.substeps ?? []).map((sub) => ({
        ...sub,
        sidebar_description: resolveDescription(sub.code, sub.sidebar_description, sub.due_date)
      }))
    }));

    const head = shouldIncludeIntro ? [INTRO_STEP_DEFAULT_DEFINITION] : [];
    const tail = shouldIncludeReview ? [REVIEW_STEP_DEFAULT_DEFINITION] : [];
    return [...head, ...enrichedSteps, ...tail];
  }, [
    initialSteps,
    componentStepsConfig,
    timezone,
    instance?.hard_go_live_date,
    shouldIncludeIntro,
    shouldIncludeReview
  ]);

  const {
    stepsState,
    activeStep,
    activeSubstep,
    activateStep,
    expandStep,
    collapseStep,
    navigateToSubstep,
    navigateTo,
    goNext,
    goPrev,
    canGoPrev
  } = useStepper(orchestratedSteps, { syncWithUrl, baseUrl, skipInitialUrlSync });

  // Guard against navigateTo identity changing mid-flight before the parent
  // has cleared navigateToCode — without this, the effect can re-fire and
  // dispatch the same code twice.
  const handledCodeRef = useRef(null);
  useEffect(() => {
    if (!navigateToCode) {
      handledCodeRef.current = null;
      return;
    }
    if (handledCodeRef.current === navigateToCode) return;
    handledCodeRef.current = navigateToCode;
    navigateTo(navigateToCode);
    onNavigateToCodeHandled?.();
  }, [navigateToCode, navigateTo, onNavigateToCodeHandled]);

  const activateStepWithHiddenCheck = useCallback(
    (stepCode) => {
      if (hiddenSubstepCodes.size === 0) {
        activateStep(stepCode);
        return;
      }
      const step = stepsState.find((s) => s.code === stepCode);
      const firstVisible = step?.substeps?.find((sub) => !hiddenSubstepCodes.has(sub.code));
      if (firstVisible && hiddenSubstepCodes.has(step?.substeps?.[0]?.code)) {
        navigateToSubstep(firstVisible.code);
      } else {
        activateStep(stepCode);
      }
    },
    [activateStep, navigateToSubstep, stepsState, hiddenSubstepCodes]
  );

  const orchestratorData = useMemo(
    () => ({
      instanceId: instance?.id,
      wizardKind,
      practiceId: instance?.instance_id,
      stepCode: activeStep?.code,
      substepCode: activeSubstep?.code,
      stepId: activeStep?.step_id ?? activeStep?.id,
      substepId: activeSubstep?.substep_id ?? activeSubstep?.id,
      originalStepId: activeSubstep?.original_step_id ?? null,
      stepName: activeStep?.name,
      substepName: activeSubstep?.name,
      substepStatus: activeSubstep?.status,
      handlerStatus: activeSubstep?.status
    }),
    [instance?.id, instance?.instance_id, wizardKind, activeStep, activeSubstep]
  );

  const progressValue = useMemo(() => {
    const n = Number(instance?.progress);
    return Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : 0;
  }, [instance?.progress]);

  return {
    instance,
    phases,
    stepsState,
    activeStep,
    activeSubstep,
    expandStep,
    collapseStep,
    navigateToSubstep,
    goNext,
    goPrev,
    canGoPrev,
    activateStepWithHiddenCheck,
    orchestratorData,
    progressValue
  };
};
